import React, { useEffect, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Avatar,
  Typography,
  Box,
  Badge,
} from '@mui/material';
import { getDoc, doc, updateDoc } from '@firebase/firestore';
import { db } from '../service/Firebase-config';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import { useAuth } from '../service/AuthContext';

const PopoverMessage = ({
  notificationListId,
  isNotificationListOpen,
  notificationAnchorEl,
  closeNotificationList,
  newUpdates,
}) => {
  const { userInfo } = useAuth();
  const { selectedUser, setSelectedUser, setTotalStars } = useGlobalContext();
  const { setOpenEditHw, selectedHw, setSelectedHw, setRating } = useHomeworkContext();
  const [showDismissAll, setShowDismissAll] = useState(false);

  useEffect(() => {
    if (selectedHw) {
      setOpenEditHw(true);
    }
  }, [selectedHw, setOpenEditHw]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        setShowDismissAll(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setShowDismissAll(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Group updates by type, homeworkId, and studentId
  const groupedUpdates = newUpdates.reduce((acc, update) => {
    const key = `${update.type}-${update.homeworkId}-${update.studentId}`;
    if (!acc[key]) {
      acc[key] = { ...update, count: 0 };
    }
    acc[key].count += 1;
    return acc;
  }, {});

  const handleDeleteUpdate = async (updateToDelete) => {
    const studentDoc = await getDoc(doc(db, 'students', updateToDelete.studentId));
    const studentData = studentDoc.data();

    setSelectedUser(studentData);
    setTotalStars(studentData.totalStars);
    const hwDoc = await getDoc(
      doc(db, `students/${updateToDelete.studentId}/homeworks`, updateToDelete.homeworkId)
    );
    const hwData = { ...hwDoc.data(), id: hwDoc.id };

    setSelectedHw(hwData);
    setRating(hwData.rating);
    closeNotificationList();

    if (userInfo?.role === 'student') {
      const homeworkRef = doc(db, `students/${selectedUser.uid}/homeworks/`, hwData.id);
      await updateDoc(homeworkRef, { isHwOpen: true, isNewComment: false });
    }

    let ref;
    if (updateToDelete.type === 'comment' && updateToDelete.from === 'student') {
      ref = doc(db, 'teachers', updateToDelete.teacherId);
    } else if (updateToDelete.type === 'comment' && updateToDelete.from === 'teacher') {
      ref = doc(db, 'students', updateToDelete.studentId);
    } else if (updateToDelete.type === 'homework') {
      ref = doc(db, 'students', updateToDelete.studentId);
    } else if (updateToDelete.type === 'adminComment') {
      ref = doc(db, 'teachers', updateToDelete.teacherId);
    } else if (updateToDelete.type === 'fileUploadByStudent') {
      ref = doc(db, 'teachers', updateToDelete.teacherId);
    } else {
      console.log('Failed');
      return;
    }

    const docSnapshot = await getDoc(ref);
    if (docSnapshot.exists()) {
      const newUpdates = docSnapshot.data().newUpdate.filter(
        (update) => update.homeworkId !== updateToDelete.homeworkId
      );
      await updateDoc(ref, { newUpdate: newUpdates });
    }
  };

  const handleDismissAll = async () => {
    try {
      let ref = null;
      if (userInfo?.role === 'student' && userInfo?.uid) {
        ref = doc(db, 'students', userInfo.uid);
      } else if (userInfo?.role !== 'teacher' && userInfo?.uid) {
        ref = doc(db, 'teachers', userInfo.uid);
      }

      if (!ref) return;

      await updateDoc(ref, { newUpdate: [] });
      closeNotificationList();
    } catch (error) {
      console.error('Failed to dismiss all updates:', error);
    }
  };

  return (
    <Popover
      id={notificationListId}
      open={isNotificationListOpen}
      anchorEl={notificationAnchorEl}
      onClose={closeNotificationList}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box p={2} sx={{ maxHeight: 400, overflowY: 'auto', minWidth: 320 }}>
        {Object.keys(groupedUpdates).length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Notifications
            </Typography>

            {showDismissAll && (
              <Typography
                variant="caption"
                onClick={handleDismissAll}
                sx={{
                  fontSize: 11,
                  color: 'text.disabled',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    color: 'text.secondary',
                    textDecoration: 'underline',
                  },
                }}
              >
                Dismiss all
              </Typography>
            )}
          </Box>
        )}

        {Object.keys(groupedUpdates).length === 0 ? (
          <Typography>No new updates</Typography>
        ) : (
          <List>
            {Object.values(groupedUpdates).map((update, index) => {
              const isCommentFromStudent =
                update.type === 'comment' && update.from === 'student';
              const isCommentFromTeacher =
                update.type === 'comment' && update.from === 'teacher';
              const isHomework = update.type === 'homework';
              const isFileUploadByStudent = update.type === 'fileUploadByStudent';

              const displayName = isCommentFromStudent || isFileUploadByStudent
                ? update.student?.displayName
                : isCommentFromTeacher || isHomework
                  ? update.teacher?.displayName
                  : 'Admin';

              const photoUrl = isCommentFromStudent || isFileUploadByStudent
                ? update.student?.photoUrl
                : isCommentFromTeacher || isHomework
                  ? update.teacher?.photoUrl
                  : undefined;

              return (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <Badge
                      badgeContent={update.count > 1 ? update.count : 0}
                      color="primary"
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar src={photoUrl} alt={displayName}>
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={displayName}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          displayName?.charAt(0)
                        )}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ flexGrow: 1 }}>
                          {update.homework.subject}
                        </Typography>
                      </Box>
                    }
                    secondary={`${displayName} - ${
                      update.type === 'comment'
                        ? 'New Message'
                        : update.type === 'homework'
                          ? 'New Homework'
                          : update.type === 'adminComment'
                            ? 'New Admin Comment'
                            : 'File Upload'
                    }`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteUpdate(update)}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Popover>
  );
};

export default PopoverMessage;